import {
  CreatePartnerSkuItemGroupRequest,
  CreatePartnerSkuItemGroupResponse,
  GetPartnerSkuItemGroupsResponse,
  UpdatePartnerSkuItemGroupRequest,
  UpdatePartnerSkuItemGroupResponse,
  CreatePartnerSkuItemSubGroupRequest,
  CreatePartnerSkuItemSubGroupResponse,
  GetPartnerSkuItemSubGroupsResponse,
  UpdatePartnerSkuItemSubGroupRequest,
  UpdatePartnerSkuItemSubGroupResponse,
  CreatePartnerSkuItemRequest,
  CreatePartnerSkuItemResponse,
  GetPartnerSkuItemsResponse,
  UpdatePartnerSkuItemRequest,
  UpdatePartnerSkuItemResponse,
  LinkSkuItemToSubGroupRequest,
  LinkSkuItemToSubGroupResponse,
  UnlinkSkuItemFromSubGroupResponse,
} from '@classpass/protobuf-definitions/lib/web/grpc/inventory/manual_partners';
import { CreateFoodAndBeverageItemPricingCaseRequest } from '@classpass/protobuf-definitions/lib/web/models/partners/fb_item_pricing_case';

import URLS from 'constants/urls';
import { transformKeysToCamelCase, transformKeysToSnakeCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { getItemCategoryIds, getItemId, getSubgroupId, toComparableId } from './SkuMenuData.normalizers';

const parseProtoResponse = (response, parser) => transformKeysToCamelCase(parser.fromJSON(response.data));

const parseGroupsResponse = response => {
  const parsed = parseProtoResponse(response, GetPartnerSkuItemGroupsResponse);
  const rawGroups = response.data?.groups || [];
  if (parsed.groups && rawGroups.length) {
    const partnerStateById = rawGroups.reduce((acc, raw) => {
      if (raw?.partner_state) acc[raw.id] = raw.partner_state;
      return acc;
    }, {});
    parsed.groups = parsed.groups.map(group =>
      partnerStateById[group.id] ? { ...group, partnerState: partnerStateById[group.id] } : group
    );
  }
  return parsed;
};
const parseSubgroupsResponse = response => {
  const parsed = parseProtoResponse(response, GetPartnerSkuItemSubGroupsResponse);
  const rawSubGroups = response.data?.sub_groups || [];
  if (parsed.subGroups && rawSubGroups.length) {
    const partnerStateById = rawSubGroups.reduce((acc, raw) => {
      if (raw?.partner_state) acc[raw.id] = raw.partner_state;
      return acc;
    }, {});
    parsed.subGroups = parsed.subGroups.map(sg =>
      partnerStateById[sg.id] ? { ...sg, partnerState: partnerStateById[sg.id] } : sg
    );
  }
  return parsed;
};
const parseItemsResponse = response => {
  const parsed = parseProtoResponse(response, GetPartnerSkuItemsResponse);
  const rawItems = response.data?.items || [];
  if (parsed.items && rawItems.length) {
    const partnerStateById = rawItems.reduce((acc, raw) => {
      if (raw?.partner_state) acc[raw.id] = raw.partner_state;
      return acc;
    }, {});
    parsed.items = parsed.items.map(item =>
      partnerStateById[item.id] ? { ...item, partnerState: partnerStateById[item.id] } : item
    );
  }
  return parsed;
};

const parseCreateGroupResponse = response => {
  const parsed = parseProtoResponse(response, CreatePartnerSkuItemGroupResponse);
  if (!parsed.group?.id && response.data?.id) {
    return parseProtoResponse({ data: { group: response.data } }, CreatePartnerSkuItemGroupResponse);
  }
  return parsed;
};
const parseUpdateGroupResponse = response => {
  const parsed = parseProtoResponse(response, UpdatePartnerSkuItemGroupResponse);
  if (!parsed.group?.id && response.data?.id) {
    return parseProtoResponse({ data: { group: response.data } }, UpdatePartnerSkuItemGroupResponse);
  }
  return parsed;
};
const parseCreateSubgroupResponse = response => {
  const parsed = parseProtoResponse(response, CreatePartnerSkuItemSubGroupResponse);
  if (!parsed.subGroup?.id && response.data?.id) {
    return parseProtoResponse({ data: { sub_group: response.data } }, CreatePartnerSkuItemSubGroupResponse);
  }
  return parsed;
};
const parseUpdateSubgroupResponse = response => {
  const parsed = parseProtoResponse(response, UpdatePartnerSkuItemSubGroupResponse);
  if (!parsed.subGroup?.id && response.data?.id) {
    return parseProtoResponse({ data: { sub_group: response.data } }, UpdatePartnerSkuItemSubGroupResponse);
  }
  return parsed;
};
// Unlike the other endpoints, the create-item API gives us the item at the top
// level of response.data instead of inside an { item: … } wrapper. We nest it
// manually so the proto parser sees the shape it expects.
const parseCreateItemResponse = response =>
  parseProtoResponse({ data: { item: response.data } }, CreatePartnerSkuItemResponse);
const parseUpdateItemResponse = response => {
  const parsed = parseProtoResponse(response, UpdatePartnerSkuItemResponse);
  if (!parsed.item?.id && response.data?.id) {
    return parseProtoResponse({ data: { item: response.data } }, UpdatePartnerSkuItemResponse);
  }
  return parsed;
};
const parseLinkResponse = response => parseProtoResponse(response, LinkSkuItemToSubGroupResponse);
const parseUnlinkResponse = response => parseProtoResponse(response, UnlinkSkuItemFromSubGroupResponse);

const getRelationItemId = relation => toComparableId(relation?.itemId);
const getRelationSubgroupId = relation => toComparableId(relation?.subGroupId);

const enrichItemsWithSubgroupRelations = (items, relations) => {
  if (!Array.isArray(items) || items.length === 0 || !Array.isArray(relations) || relations.length === 0) {
    return items;
  }

  const relationSubgroupsByItemId = relations.reduce((acc, relation) => {
    const itemId = getRelationItemId(relation);
    const subGroupId = getRelationSubgroupId(relation);
    if (!itemId || !subGroupId) {
      return acc;
    }

    const current = acc[itemId] || new Set();
    current.add(subGroupId);
    acc[itemId] = current;
    return acc;
  }, {});

  return items.map(item => {
    const itemId = getItemId(item);
    if (!itemId) {
      return item;
    }

    const relationSubgroupIds = relationSubgroupsByItemId[itemId];
    if (!relationSubgroupIds || relationSubgroupIds.size === 0) {
      return item;
    }

    const mergedSubgroupIds = Array.from(new Set([...getItemCategoryIds(item), ...relationSubgroupIds]));
    if (mergedSubgroupIds.length === 0) {
      return item;
    }

    const existingDirectSubgroupId = item?.subGroupId;

    return {
      ...item,
      subGroupIds: mergedSubgroupIds,
      ...(existingDirectSubgroupId ? {} : { subGroupId: mergedSubgroupIds[0] }),
    };
  });
};

export async function submitSkuMenuPricing({ venueId, currencyCode }, { itemName, menuPrice, linkToMenu }) {
  const url = URLS.items.submitMenuPricing(venueId);
  const requestBody = CreateFoodAndBeverageItemPricingCaseRequest.toJSON({
    item_name: itemName,
    menu_price: { currency_code: currencyCode, formatted_price_in_major_units: menuPrice },
    menu_link: linkToMenu,
  });

  return httpClient.post(url, requestBody, {
    ignoreInternalError: true,
  });
}

// Fetch helpers (read operations)

export async function fetchSkuGroups(venueId) {
  const url = URLS.sku.groups(venueId);
  try {
    const response = await httpClient.get(url);
    const payload = parseGroupsResponse(response);
    return payload.groups || [];
  } catch (error) {
    reportError('Error fetching SKU groups', error);
    throw error;
  }
}

export async function fetchSkuSubgroups(venueId) {
  const url = URLS.sku.subgroups(venueId);
  try {
    const response = await httpClient.get(url);
    const payload = parseSubgroupsResponse(response);
    return payload.subGroups || [];
  } catch (error) {
    reportError('Error fetching SKU subgroups', error);
    throw error;
  }
}

export async function fetchSkuItems(venueId) {
  const url = URLS.sku.items(venueId);
  try {
    const response = await httpClient.get(url);
    const payload = parseItemsResponse(response);
    return payload.items || [];
  } catch (error) {
    reportError('Error fetching SKU items', error);
    throw error;
  }
}

const mergeItemsById = items => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const mergedById = {};
  const itemsWithoutId = [];

  items.forEach(item => {
    const itemId = getItemId(item);
    if (!itemId) {
      itemsWithoutId.push(item);
      return;
    }

    const existingItem = mergedById[itemId];
    if (!existingItem) {
      mergedById[itemId] = item;
      return;
    }

    const mergedSubgroupIds = Array.from(new Set([...getItemCategoryIds(existingItem), ...getItemCategoryIds(item)]));
    const existingDirectSubgroupId = existingItem?.subGroupId;
    const nextDirectSubgroupId = item?.subGroupId;
    const resolvedDirectSubgroupId = existingDirectSubgroupId || nextDirectSubgroupId || mergedSubgroupIds[0];

    mergedById[itemId] = {
      ...existingItem,
      ...item,
      ...(mergedSubgroupIds.length > 0 ? { subGroupIds: mergedSubgroupIds } : {}),
      ...(resolvedDirectSubgroupId ? { subGroupId: resolvedDirectSubgroupId } : {}),
    };
  });

  return [...Object.values(mergedById), ...itemsWithoutId];
};

const appendSubgroupQueryParam = (url, subGroupId) => {
  if (!subGroupId) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}sub_group_id=${encodeURIComponent(subGroupId)}`;
};

export async function fetchSkuItemsBySubgroup(venueId, subGroupId) {
  const scopedUrl = appendSubgroupQueryParam(URLS.sku.items(venueId), subGroupId);
  try {
    const response = await httpClient.get(scopedUrl);
    const payload = parseItemsResponse(response);
    const items = payload.items || [];
    const syntheticRelations = items
      .map(item => ({ itemId: getItemId(item), subGroupId }))
      .filter(relation => relation.itemId && relation.subGroupId);
    return enrichItemsWithSubgroupRelations(items, syntheticRelations);
  } catch (error) {
    reportError('Error fetching SKU items by subgroup', error);
    throw error;
  }
}

export async function fetchSkuItemsForSubgroups(venueId, subgroups = []) {
  const subGroupIds = Array.from(new Set(subgroups.map(getSubgroupId).filter(Boolean)));
  if (subGroupIds.length === 0) {
    return fetchSkuItems(venueId);
  }

  const subgroupItems = await Promise.all(subGroupIds.map(subGroupId => fetchSkuItemsBySubgroup(venueId, subGroupId)));
  return mergeItemsById(subgroupItems.flat());
}

// CRUD helpers (write operations)

// BusinessHours contains nested proto types (DayOfWeek enum, TimeOfDay message)
// that don't serialize cleanly from plain JS via toJSON alone. The fromJSON step
// converts raw JS into proper proto instances so toJSON can serialize them correctly.
function serializeGroupRequest(ProtoType, fields, businessHours) {
  return ProtoType.toJSON(
    ProtoType.fromJSON({
      ...fields,
      ...(businessHours !== undefined ? { business_hours: transformKeysToSnakeCase(businessHours) } : {}),
    })
  );
}

export async function createSkuGroup(venueId, { name, businessHours, enabled }) {
  try {
    const url = URLS.sku.groups(venueId);
    const requestBody = serializeGroupRequest(
      CreatePartnerSkuItemGroupRequest,
      { venue_id: venueId, name, enabled },
      businessHours
    );
    const response = await httpClient.post(url, requestBody);
    return parseCreateGroupResponse(response);
  } catch (error) {
    reportError('Error creating SKU group', error);
    throw error;
  }
}

export async function updateSkuGroup(venueId, groupId, { name, businessHours, enabled, partnerState }) {
  try {
    const url = URLS.sku.group(venueId, groupId);
    const requestBody = {
      ...serializeGroupRequest(UpdatePartnerSkuItemGroupRequest, { group_id: groupId, name, enabled }, businessHours),
      ...(partnerState !== undefined ? { partner_state: partnerState } : {}),
    };
    const response = await httpClient.put(url, requestBody);
    return parseUpdateGroupResponse(response);
  } catch (error) {
    reportError('Error updating SKU group', error);
    throw error;
  }
}

export async function createSkuSubgroup(venueId, { groupId, name, ordinal, enabled }) {
  try {
    const url = URLS.sku.subgroups(venueId);
    const requestBody = CreatePartnerSkuItemSubGroupRequest.toJSON({
      group_id: groupId,
      name,
      ordinal,
      enabled,
    });
    const response = await httpClient.post(url, requestBody);
    return parseCreateSubgroupResponse(response);
  } catch (error) {
    reportError('Error creating SKU subgroup', error);
    throw error;
  }
}

export async function updateSkuSubgroup(venueId, subGroupId, { name, ordinal, enabled, partnerState }) {
  try {
    const url = URLS.sku.subgroup(venueId, subGroupId);
    const requestBody = {
      ...UpdatePartnerSkuItemSubGroupRequest.toJSON({
        sub_group_id: subGroupId,
        name,
        ordinal,
        enabled,
      }),
      ...(partnerState !== undefined ? { partner_state: partnerState } : {}),
    };
    const response = await httpClient.put(url, requestBody);
    return parseUpdateSubgroupResponse(response);
  } catch (error) {
    reportError('Error updating SKU subgroup', error);
    throw error;
  }
}

export async function createSkuItem(venueId, { name, description, enabled, soldOut }) {
  try {
    const url = URLS.sku.items(venueId);
    const requestBody = CreatePartnerSkuItemRequest.toJSON({
      venue_id: venueId,
      name,
      description,
      enabled,
      sold_out: soldOut,
    });
    const response = await httpClient.post(url, requestBody);
    return parseCreateItemResponse(response);
  } catch (error) {
    reportError('Error creating SKU item', error);
    throw error;
  }
}

export async function updateSkuItem(venueId, itemId, { name, description, enabled, soldOut, partnerState }) {
  try {
    const url = URLS.sku.item(venueId, itemId);
    const requestBody = {
      ...UpdatePartnerSkuItemRequest.toJSON({
        item_id: itemId,
        name,
        description,
        enabled,
        sold_out: soldOut,
      }),
      ...(partnerState !== undefined ? { partner_state: partnerState } : {}),
    };
    const response = await httpClient.put(url, requestBody);
    return parseUpdateItemResponse(response);
  } catch (error) {
    reportError('Error updating SKU item', error);
    throw error;
  }
}

export async function linkItemToSubgroup(venueId, subGroupId, itemId, ordinal) {
  try {
    const url = URLS.sku.linkItem(venueId, subGroupId, itemId);
    const requestBody = LinkSkuItemToSubGroupRequest.toJSON({
      item_id: itemId,
      sub_group_id: subGroupId,
      ordinal,
    });
    const response = await httpClient.post(url, requestBody);
    return parseLinkResponse(response);
  } catch (error) {
    reportError('Error linking SKU item to subgroup', error);
    throw error;
  }
}

export async function unlinkItemFromSubgroup(venueId, subGroupId, itemId) {
  try {
    const url = URLS.sku.linkItem(venueId, subGroupId, itemId);
    const response = await httpClient.delete(url);
    return parseUnlinkResponse(response);
  } catch (error) {
    reportError('Error unlinking SKU item from subgroup', error);
    throw error;
  }
}
